import { LegalEntityDto } from "api/actor";
import { SearchPersonResult } from "components/domain/SearchBar/types";
import { NsKey } from "enums/NsKey";
import { SubsidiaryForm } from "features/new-actor/components/subsidiaries/SubsidiaryForm";
import { SubsidiaryFormValues } from "features/new-actor/types/optim/subsidiaryFormValues";
import { getAttachRefusal } from "features/new-actor/utils/subsidiariesGuards";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { FC, useCallback, useState } from "react";
import { ExistingCompanySearch } from "./ExistingCompanySearch";
import { LegalProtectionPanelDivider } from "./LegalProtectionPanelDivider";
import { InheritedIdentityBlock, renderSubsidiaryPanelLayout } from "./SubsidiaryPanelFields";

interface AddSubsidiaryPanelProps {
  /** Company the subsidiary is attached to. */
  legalEntityId: string;
  parentRegistrationNumber?: string | null;
  subsidiaries: SubsidiaryFormValues[];
  /** No company picked in the search: a new subsidiary is created. */
  onCreate: (values: SubsidiaryFormValues) => Promise<void>;
  /** A company picked in the search: it is attached, only its figures are entered. */
  onAttach: (values: SubsidiaryFormValues) => Promise<void>;
}

/**
 * Content of the add panel: the search of an existing company on top, then the subsidiary form.
 * Rendered outside the route by `openPanel`: everything it needs comes as props.
 */
export const AddSubsidiaryPanel: FC<AddSubsidiaryPanelProps> = ({
  legalEntityId,
  parentRegistrationNumber,
  subsidiaries,
  onCreate,
  onAttach
}) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const [picked, setPicked] = useState<SubsidiaryFormValues | undefined>(undefined);
  const [refusal, setRefusal] = useState<string | null>(null);

  // The attach refusals are checked as soon as the company is picked, as the back does.
  const handleSelect = useCallback((person: SearchPersonResult) => {
    const legalEntity = person.data as LegalEntityDto;
    const registrationNumber = person.registrationNumber ?? legalEntity.registrationNumber;

    const refused = getAttachRefusal(
      { id: person.id, registrationNumber },
      { id: legalEntityId, registrationNumber: parentRegistrationNumber },
      subsidiaries
    );
    if (refused) {
      setPicked(undefined);
      setRefusal(t(`ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.ERRORS.${refused}`));
      return;
    }

    setRefusal(null);
    setPicked({
      id: '',
      companyName: person.name || legalEntity.companyName,
      registrationNumber,
      revenue: '',
      employeesCount: '',
      fiscalYearEnd: '',
      legalPersonId: person.id
    });
  }, [legalEntityId, parentRegistrationNumber, subsidiaries, t]);

  return (
    <div className="flex flex-col gap-7">
      <ExistingCompanySearch onSelect={handleSelect} error={refusal} isSelected={Boolean(picked)} />

      <LegalProtectionPanelDivider
        label={picked
          ? t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.SUBSIDIARY_SELECTED")
          : t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.PANEL.OR_ENTER_SUBSIDIARY")}
      />

      <div className="flex flex-col gap-5">
        {picked && (
          // Attach: the identity belongs to the picked company and is not typed here. Its values
          // stay in the form, which saves them unchanged.
          <InheritedIdentityBlock companyName={picked.companyName} registrationNumber={picked.registrationNumber} />
        )}

        <SubsidiaryForm
          // A picked company remounts the form: it reopens on the attached row, read-only identity.
          key={picked?.legalPersonId ?? 'new'}
          inPanel
          handleSubmit={picked ? onAttach : onCreate}
          pendingSubsidiary={picked}
          parentRegistrationNumber={parentRegistrationNumber}
          subsidiaries={subsidiaries}
          renderLayout={renderSubsidiaryPanelLayout(Boolean(picked))}
        />
      </div>
    </div>
  );
};
